<template>
  <Card :class="['response-viewer', isError ? 'error-response' : 'success-response']">
    <div class="p-4">
      <!-- Header with copy button -->
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">
          {{ isError ? 'Error Response' : 'Response' }}
        </h3>
        <Button variant="ghost" size="sm" @click="copyToClipboard" :disabled="!response">
          <span class="text-xs">{{ copied ? 'Copied!' : 'Copy' }}</span>
        </Button>
      </div>

      <!-- Empty state -->
      <div v-if="!response" class="text-gray-500 dark:text-gray-400 text-sm italic">
        No response yet. Execute a tool to see results.
      </div>

      <!-- Response content with scrolling for large responses -->
      <div v-else class="response-content">
        <pre
          :class="['response-json', isError ? 'error-json' : 'success-json']"
        ><code>{{ formattedResponse }}</code></pre>
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { Card, Button } from '@asabdev/asab-ui';
  import type { ToolResponse } from '../../types';

  interface Props {
    response: ToolResponse | null;
  }

  const props = defineProps<Props>();

  const copied = ref(false);

  const isError = computed(() => {
    return props.response ? !props.response.success : false;
  });

  const formattedResponse = computed(() => {
    if (!props.response) return '';

    try {
      return JSON.stringify(props.response, null, 2);
    } catch {
      return String(props.response);
    }
  });

  const copyToClipboard = async () => {
    if (!props.response) return;

    try {
      await navigator.clipboard.writeText(formattedResponse.value);
      copied.value = true;
      setTimeout(() => {
        copied.value = false;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };
</script>

<style scoped>
  .response-viewer {
    @apply w-full;
  }

  .error-response {
    @apply border-red-300 dark:border-red-700;
  }

  .success-response {
    @apply border-gray-200 dark:border-gray-700;
  }

  .response-content {
    @apply max-h-96 overflow-auto;
  }

  .response-json {
    @apply text-xs font-mono whitespace-pre-wrap break-words p-3 rounded;
  }

  .success-json {
    @apply bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100;
  }

  .error-json {
    @apply bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-200;
  }

  /* Scrollbar styling */
  .response-content::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  .response-content::-webkit-scrollbar-track {
    @apply bg-gray-100 dark:bg-gray-800 rounded;
  }

  .response-content::-webkit-scrollbar-thumb {
    @apply bg-gray-300 dark:bg-gray-600 rounded;
  }

  .response-content::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400 dark:bg-gray-500;
  }
</style>
